import { ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { useAppDispatch, useAppSelector } from '../hooks.ts';
import { useStore } from '../pages/workspace/hooks.ts';
import { loadBakedModelFile, setActiveViewMode } from '../store/reducers/workspace.reducer.ts';
import { ViewModes } from '../types/view.ts';
import { FRONTEND_MEASUREMENT_CALCULATION_FLAG } from '../utils/measurementCalculation/types.ts';
import { seedUseGltfCache } from '../utils/seedUseGltfCache.ts';

/* eslint-disable react-refresh/only-export-components */

type ViewMode = keyof ViewModes;

interface ViewModeTransitionContextType {
  isTransitioning: boolean;
  pendingMode: ViewMode | null;
  transitionToMode: (mode: ViewMode) => void;
}

const ViewModeTransitionContext = createContext<ViewModeTransitionContextType | null>(null);

// Hold the blur in place after the new scene mounts so React/R3F can commit
// the new tree and the renderer can paint the first frame.
const POST_SWITCH_HOLD_MS = 500;
// Upper bound for waiting on backend assets to download.
const READINESS_TIMEOUT_MS = 15000;
// Poll cadence while waiting for Redux state to reflect that downloads finished.
const POLL_INTERVAL_MS = 100;

const wait = (ms: number) => new Promise<void>((resolve) => window.setTimeout(resolve, ms));

const nextFrame = () =>
  new Promise<void>((resolve) => {
    window.requestAnimationFrame(() => resolve());
  });

export const ViewModeTransitionProvider = ({ children }: { children: ReactNode }) => {
  const dispatch = useAppDispatch();
  const { activeViewModes, activeVersion } = useStore();
  const frontendCalcEnabled = useAppSelector((s) =>
    s.featureFlags.userFeatureFlags.includes(FRONTEND_MEASUREMENT_CALCULATION_FLAG),
  );

  const [isTransitioning, setIsTransitioning] = useState(false);
  const [pendingMode, setPendingMode] = useState<ViewMode | null>(null);

  // Always read the freshest version inside async waits without re-creating callbacks
  const versionRef = useRef(activeVersion);
  useEffect(() => {
    versionRef.current = activeVersion;
  }, [activeVersion]);

  // Read the freshest flag inside async waits without re-creating callbacks.
  const frontendCalcEnabledRef = useRef(frontendCalcEnabled);
  useEffect(() => {
    frontendCalcEnabledRef.current = frontendCalcEnabled;
  }, [frontendCalcEnabled]);

  // Token bumped on each new transition so any in-flight wait can detect it's stale
  const transitionTokenRef = useRef(0);

  /** Poll Redux state until predicate returns true, with timeout. */
  const waitFor = useCallback(async (predicate: () => boolean, token: number): Promise<boolean> => {
    const startedAt = Date.now();
    while (!predicate()) {
      if (transitionTokenRef.current !== token) return false;
      if (Date.now() - startedAt > READINESS_TIMEOUT_MS) return false;
      await wait(POLL_INTERVAL_MS);
    }
    return true;
  }, []);

  /** Force the GLB to parse before we swap modes so <ModelTexture/> mounts ready. */
  const preloadBakedModel = useCallback((url: string) => seedUseGltfCache(url), []);

  const prepareForMode = useCallback(
    async (mode: ViewMode, token: number): Promise<boolean> => {
      if (mode === 'render') {
        // Kick off the backend fetch if we don't already have a blob URL
        if (!versionRef.current?.bakedModelUrl) {
          dispatch(loadBakedModelFile());
        }
        const ok = await waitFor(() => !!versionRef.current?.bakedModelUrl, token);
        if (!ok) return false;
        if (transitionTokenRef.current !== token) return false;
        try {
          await preloadBakedModel(versionRef.current!.bakedModelUrl!);
        } catch (err) {
          console.error('Failed to preload baked model during view mode transition', err);
        }
        return true;
      }

      if (mode === 'measurement') {
        // The frontend measurement flow has no backend `version.measurementData`
        // to wait for — it computes its own data AFTER the view swaps (the in-canvas
        // runner fires once `showMeasurements` flips true). Waiting here would just
        // burn the full READINESS_TIMEOUT before swapping. Flip immediately instead.
        if (frontendCalcEnabledRef.current) return true;
        return waitFor(
          () => !!versionRef.current?.measurementData && versionRef.current.measurementData.length > 0,
          token,
        );
      }

      // object / realTimeRender — no async asset to wait for
      return true;
    },
    [dispatch, preloadBakedModel, waitFor],
  );

  const transitionToMode = useCallback(
    (mode: ViewMode) => {
      if (activeViewModes?.[mode]) return;

      const token = ++transitionTokenRef.current;
      setPendingMode(mode);
      setIsTransitioning(true);

      void (async () => {
        // Let the overlay paint before we touch anything expensive
        await nextFrame();
        await nextFrame();

        const ready = await prepareForMode(mode, token);
        if (transitionTokenRef.current !== token) return;
        // Even on timeout we still flip — better to show the new mode than trap the user
        if (!ready) {
          console.warn(`View mode "${mode}" not ready after ${READINESS_TIMEOUT_MS}ms; swapping anyway`);
        }

        dispatch(setActiveViewMode(mode));

        // Give R3F two frames to commit, then hold the blur briefly so the first
        // real frame of the new scene replaces the old one underneath
        await nextFrame();
        await nextFrame();
        await wait(POST_SWITCH_HOLD_MS);

        if (transitionTokenRef.current !== token) return;
        setIsTransitioning(false);
        setPendingMode(null);
      })();
    },
    [activeViewModes, dispatch, prepareForMode],
  );

  // Cancel any in-flight transition on unmount — bump the live counter so
  // pending promises see they're stale.
  useEffect(() => {
    const ref = transitionTokenRef;
    return () => {
      ref.current++;
    };
  }, []);

  const value = useMemo(
    () => ({ isTransitioning, pendingMode, transitionToMode }),
    [isTransitioning, pendingMode, transitionToMode],
  );

  return <ViewModeTransitionContext.Provider value={value}>{children}</ViewModeTransitionContext.Provider>;
};

export const useViewModeTransition = (): ViewModeTransitionContextType => {
  const ctx = useContext(ViewModeTransitionContext);
  if (!ctx) {
    return {
      isTransitioning: false,
      pendingMode: null,
      transitionToMode: () => {},
    };
  }
  return ctx;
};
